"use client";

import { useState, useEffect, useRef, useCallback } from "react";
import { fetchWithAuth } from "@/lib/api-helpers";
import { useWorkspaceContextWithFallback } from "@/contexts/workspaceContext";

export interface UpcomingBot {
  _id: string;
  title?: string;
  meetingUrl: string;
  platform: string;
  botName?: string;
  scheduledTime: string;
  status: string;
  minutesUntilStart: number;
}

const POLL_INTERVAL_MS = 60_000;
const REMINDER_WINDOW_MINUTES = 15;

/**
 * Stable identity for a *meeting reminder*, independent of which workspace's
 * scheduled bot happens to back it.
 *
 * The same calendar can be connected to several workspaces, and each workspace
 * schedules its own bot for the same meeting — so one real meeting can have N
 * bot rows with N different _ids. Keying on the meeting itself (url + start
 * time) is what makes "one meeting = one banner" hold, and keeps a dismissal
 * from reappearing under a sibling bot's _id on the next poll.
 */
function meetingKey(bot: { meetingUrl?: string; scheduledTime: string; _id: string }): string {
  return bot.meetingUrl ? `${bot.meetingUrl}@${bot.scheduledTime}` : `id:${bot._id}`;
}

export function useUpcomingMeetings() {
  const [upcoming, setUpcoming] = useState<UpcomingBot[]>([]);
  const dismissedKeys = useRef<Set<string>>(new Set());

  // Scope reminders to the workspace the user is actually looking at.
  // Without a workspaceId the API returns this user's bots across EVERY
  // workspace, which surfaced one banner per workspace for the same meeting.
  const workspaceCtx = useWorkspaceContextWithFallback();
  const workspaceId = workspaceCtx?.currentWorkspace?._id;

  const check = useCallback(async () => {
    if (!workspaceId) {
      setUpcoming([]);
      return;
    }
    try {
      const res = await fetchWithAuth("/api/bot/schedule/get-all", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ workspaceId }),
      });
      const data = await res.json();
      const bots: any[] = data.scheduledBots || [];
      const now = Date.now();

      const soon = bots
        .filter((b) => {
          if (b.status !== "scheduled") return false;
          if (dismissedKeys.current.has(meetingKey(b))) return false;
          const ms = new Date(b.scheduledTime).getTime() - now;
          return ms >= 0 && ms <= REMINDER_WINDOW_MINUTES * 60 * 1000;
        })
        .map((b) => ({
          ...b,
          minutesUntilStart: Math.max(
            0,
            Math.floor((new Date(b.scheduledTime).getTime() - now) / 60_000)
          ),
        }));

      // Collapse duplicates — one banner per real meeting. Belt-and-braces
      // alongside the workspace scope above: duplicate bot rows within a
      // single workspace (retries, re-syncs) must not stack banners either.
      const byMeeting = new Map<string, UpcomingBot>();
      for (const bot of soon) {
        const key = meetingKey(bot);
        if (!byMeeting.has(key)) byMeeting.set(key, bot);
      }

      setUpcoming(
        Array.from(byMeeting.values()).sort(
          (a, b) =>
            new Date(a.scheduledTime).getTime() - new Date(b.scheduledTime).getTime()
        )
      );
    } catch {
      // silent — don't disrupt the user on poll failure
    }
  }, [workspaceId]);

  useEffect(() => {
    check();
    const id = setInterval(check, POLL_INTERVAL_MS);
    return () => clearInterval(id);
  }, [check]);

  const dismiss = useCallback((id: string) => {
    setUpcoming((prev) => {
      const target = prev.find((b) => b._id === id);
      if (target) dismissedKeys.current.add(meetingKey(target));
      return prev.filter((b) => b._id !== id);
    });
  }, []);

  return { upcoming, dismiss };
}
